import React, { useEffect, useState } from "react";
import { Box, Button, Divider, Paper, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";

import DynamicForm from "../../components/form/DynamicForm";

import { getTenantInputs } from "../../utils/tenant.inputs";
import {
  createNewTenant,
  fetchAllAnnualIncrementIntervalList,
  fetchAllTenantTypeList,
  getTenantDetailsById,
  updateTenantDetails,
} from "../../services/api/tenant";

import { fetchBuildingList } from "../../services/api/building";
import { useDispatch } from "react-redux";
import { showToast } from "../../store/slices/toastSlice";
import {
  CREATE_UPDATE_BUTTON,
  RESET_CANCEL_BUTTON,
} from "../../styles/ButtonStyles";
import { validateTenantFields } from "../../utils/validation/validation";
import { getGstDetails } from "../../services/api/setting";
import { fetchAvailableRentalProperties } from "../../services/api/rental-property";

export default function CreateUpdateTenant() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  const dispatch = useDispatch();

  const initialFormState = {
    tenant_first_name: "",
    tenant_last_name: "",
    tenant_contact: "",
    uid_number: "",
    entry_date: "",
    exit_date: "",
    annual_increment_type: "",
    annual_increment_date: "",
    building_id: "",
    property_id: "",
    rent_amount: "",
    is_gst_check: false,
    gst_amount: "0.00",
    tenant_type: "",
    electricity_per_unit_charge: "",
    monthly_electricity_bill: "",
    maintenance_charges: "",
    notes: "",
    total_rent_amount: "0.00",
  };

  const [tenantDetails, setTenantDetails] = useState(initialFormState);
  const [buildingOptions, setBuildingOptions] = useState([]);
  const [propertyOptions, setPropertyOptions] = useState([]);
  const [intervalOptions, setIntervalOptions] = useState([]);
  const [tenantType, setTenantType] = useState([]);
  const [gstPercentage, setGstPercentage] = useState(0);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const buildings = await fetchBuildingList();
        setBuildingOptions(
          buildings.data.map((b) => ({
            value: b.id.toString(),
            label: b.building_name,
          }))
        );

        const properties = await fetchAvailableRentalProperties(
          isEditMode ? id : null
        );

        if (properties.data.length > 0) {
          setPropertyOptions(properties.data);
        } else {
          setPropertyOptions([]);
        }

        if (properties.data.length > 0) {
          setPropertyOptions(properties.data); // full list for filtering
        } else {
          setPropertyOptions([]);
        }

        const intervals = await fetchAllAnnualIncrementIntervalList();
        setIntervalOptions(intervals.data);
        console.log("@@@@@@@ intervals.data :", intervals.data);

        const tenantTypeList = await fetchAllTenantTypeList();
        setTenantType(tenantTypeList.data);
      } catch (error) {
        dispatch(showToast({ message: error.message, severity: "error" }));
      }

      const gstResponse = await getGstDetails();
      if (gstResponse?.data?.length > 0) {
        setGstPercentage(gstResponse.data[0].gst_percentage);
      }
    };

    const fetchTenantDetails = async () => {
      if (isEditMode) {
        try {
          const response = await getTenantDetailsById(id);
          if (response.responseCode === 200) {
            const data = response.data;
            setTenantDetails({
              ...data,
              building_id: data.building_id?.toString() || "",
              property_id: data.property_id?.toString() || "",
            });
          }
        } catch (error) {
          dispatch(showToast({ message: error.message, severity: "error" }));
        }
      }
    };

    loadOptions();
    fetchTenantDetails();
  }, [id, isEditMode]);

  useEffect(() => {
    const { entry_date, annual_increment_type } = tenantDetails;

    if (entry_date && annual_increment_type) {
      const date = new Date(entry_date);
      date.setMonth(date.getMonth() + Number(annual_increment_type));

      setTenantDetails((prev) => ({
        ...prev,
        annual_increment_date: date.toISOString().split("T")[0],
      }));
    }
  }, [tenantDetails.entry_date, tenantDetails.annual_increment_type]);

  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;

    setTenantDetails((prev) => {
      const updated = {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };

      // Reset property selection if building changes
      if (name === "building_id") {
        updated.property_id = "";
      }

      const { gst_amount, total_rent_amount } = calculateTotalRent(updated);
      updated.gst_amount = gst_amount;
      updated.total_rent_amount = total_rent_amount;

      return updated;
    });
  };

  const calculateTotalRent = ({
    rent_amount,
    maintenance_charges,
    monthly_electricity_bill,
    is_gst_check,
  }) => {
    const rent = parseFloat(rent_amount) || 0;
    const maintenance = parseFloat(maintenance_charges) || 0;
    const monthlyElctricityBill = parseFloat(monthly_electricity_bill) || 0;

    const baseTotal = rent + maintenance;
    const gstRate = gstPercentage / 100;
    const gst = is_gst_check ? baseTotal * gstRate : 0;

    return {
      gst_amount: gst.toFixed(2),
      total_rent_amount: (baseTotal + gst + monthlyElctricityBill).toFixed(2),
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationResult = validateTenantFields(tenantDetails);
    if (!validationResult.valid) {
      dispatch(
        showToast({ message: validationResult.message, severity: "error" })
      );
      return;
    }

    const tenantPayload = {
      ...tenantDetails,
      building_id: parseInt(tenantDetails.building_id),
      property_id: parseInt(tenantDetails.property_id),
    };

    console.log("@@@@@@@@@@@ tenantPayload :", tenantPayload);
    try {
      let response;
      if (isEditMode) {
        response = await updateTenantDetails(id, tenantPayload);
      } else {
        response = await createNewTenant(tenantPayload);
      }

      if (response.responseCode === 200) {
        dispatch(showToast({ message: response.message, severity: "success" }));
        localStorage.setItem(
          isEditMode ? "editTenantSuccess" : "addNewTenantSuccess",
          response.message
        );
        navigate("/tenant-list");
      }
    } catch (error) {
      dispatch(showToast({ message: error.message, severity: "error" }));
    }
  };

  const formInputs = getTenantInputs(
    buildingOptions,
    propertyOptions,
    tenantDetails.building_id,
    intervalOptions,
    tenantType
  );

  const handleCancelOrReset = () => {
    if (isEditMode) {
      // Navigate back if editing
      navigate("/tenant-list");
    } else {
      // Reset form if adding
      setTenantDetails(initialFormState);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        mt: 4,
        px: { xs: 2, sm: 3 },
      }}
    >
      <Paper
        elevation={3}
        sx={{
          width: "100%",
          borderRadius: 3,
          p: { xs: 3, sm: 4 },
          backgroundColor: "#fff",
          boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
        }}
      >
        <Typography
          variant="h5"
          fontWeight="bold"
          sx={{ mb: 2, color: "#333" }}
        >
          {isEditMode ? "Update Tenant Details" : "Add New Tenant"}
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mt: 1, mb: 1 }}
        >
          {isEditMode
            ? "Modify the tenant information below and click update when done."
            : "Fill out the tenant details to add a new record to the system."}
        </Typography>

        <Divider sx={{ mb: 3 }} />

        <Box component="form" onSubmit={handleSubmit}>
          <DynamicForm
            inputs={formInputs}
            values={tenantDetails}
            onChange={handleChange}
          />

          <Divider />

          <Box sx={{ display: "flex", justifyContent: "flex-start", mt: 2 }}>
            <Button type="submit" variant="contained" sx={CREATE_UPDATE_BUTTON}>
              {isEditMode ? "Update Tenant" : "Create Tenant"}
            </Button>

            <Button
              type="button"
              variant="text"
              onClick={handleCancelOrReset}
              sx={RESET_CANCEL_BUTTON}
            >
              {isEditMode ? "Cancel" : "Reset"}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
